import { AnimatePresence, motion, useSpring } from "framer-motion";
import { type FC, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { useOverlay } from "../../hooks/useOverlay";
import { formatAbbrevNumber, formatUsd } from "../../utils/currency";
import {
  type ActorSpendHistory,
  type DailyBucket,
  type ModelRow,
  type StageRow,
  useActorSpendHistory,
} from "./useActorSpendHistory";

interface BurnRateModalProps {
  open: boolean;
  onClose: () => void;
}

const overlayMotion = {
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  initial: { opacity: 0 },
  transition: { duration: 0.15 },
};

const panelMotion = {
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.97, y: 8 },
  initial: { opacity: 0, scale: 0.97, y: 8 },
  transition: {
    damping: 22,
    mass: 0.8,
    stiffness: 260,
    type: "spring" as const,
  },
};

export const BurnRateModal: FC<BurnRateModalProps> = ({ open, onClose }) => {
  useOverlay(open, onClose);
  const { data, error, loading } = useActorSpendHistory(open);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          {...overlayMotion}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 backdrop-blur-sm md:p-6"
          onClick={onClose}
        >
          <motion.div
            {...panelMotion}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-4xl overflow-hidden border border-emerald-500/40 bg-white shadow-[0_0_0_1px_rgba(16,185,129,0.08),0_40px_80px_-20px_rgba(16,185,129,0.35)] dark:border-emerald-400/30 dark:bg-slate-950"
            style={{
              backgroundImage:
                "linear-gradient(rgba(16,185,129,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.04) 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          >
            {/* Corner brackets */}
            <CornerBracket position="tl" />
            <CornerBracket position="tr" />
            <CornerBracket position="bl" />
            <CornerBracket position="br" />

            {/* Scanline overlay */}
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(0deg, rgba(16,185,129,1) 0 1px, transparent 1px 3px)",
              }}
            />

            {/* Top accent */}
            <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-emerald-400 to-transparent" />

            <div className="relative flex items-center justify-between border-b border-emerald-500/25 px-6 py-4 dark:border-emerald-400/15">
              <div>
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700 dark:text-emerald-400/70">
                  [ SPEND HISTORY — ACTOR CHAT LOOP ]
                </p>
                <p className="mt-1 font-mono text-[11px] tracking-tight text-slate-500">
                  arti.chat.v1 + cache_warm + consolidation + compact_seed ·
                  last 120 days
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="group relative flex h-7 w-7 items-center justify-center border border-emerald-500/40 font-mono text-[12px] text-emerald-700/90 hover:border-emerald-500/70 hover:text-emerald-700 dark:border-emerald-400/30 dark:text-emerald-300/80 dark:hover:border-emerald-400/60 dark:hover:text-emerald-300"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="relative max-h-[72vh] overflow-y-auto px-6 py-5">
              {loading && <LoadingView />}
              {error && !loading && <ErrorView message={error} />}
              {!loading && !error && data && <SpendDashboard data={data} />}
              {!loading && !error && !data && (
                <div className="py-8 text-center font-mono text-[11px] tracking-widest text-slate-500">
                  NO DATA
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

const CornerBracket: FC<{ position: "tl" | "tr" | "bl" | "br" }> = ({
  position,
}) => {
  const map: Record<typeof position, string> = {
    bl: "bottom-1 left-1 border-b border-l",
    br: "bottom-1 right-1 border-b border-r",
    tl: "top-1 left-1 border-l border-t",
    tr: "top-1 right-1 border-r border-t",
  };
  return (
    <span
      aria-hidden
      className={`pointer-events-none absolute h-2 w-2 border-emerald-400/60 ${map[position]}`}
    />
  );
};

const LoadingView: FC = () => (
  <div className="flex flex-col items-center justify-center gap-3 py-16 font-mono text-[11px] uppercase tracking-[0.25em] text-emerald-700/80 dark:text-emerald-300/70">
    <div className="flex gap-1">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="h-2 w-2 bg-emerald-400"
          animate={{ opacity: [0.2, 1, 0.2] }}
          transition={{ delay: i * 0.15, duration: 0.9, repeat: Infinity }}
        />
      ))}
    </div>
    <span>LOADING HISTORY</span>
  </div>
);

const ErrorView: FC<{ message: string }> = ({ message }) => (
  <div className="border border-rose-500/40 bg-rose-500/5 p-4 font-mono text-[11px] text-rose-700 dark:text-rose-300">
    <p className="font-bold uppercase tracking-widest">[ ERROR ]</p>
    <p className="mt-1">{message}</p>
  </div>
);

const SpendDashboard: FC<{ data: ActorSpendHistory }> = ({ data }) => {
  const dayCount = data.byDay.length;
  const daySpanMs =
    data.firstBucketMs !== null && data.lastBucketMs !== null
      ? data.lastBucketMs - data.firstBucketMs + 86_400_000
      : 0;
  const calendarSpanDays =
    daySpanMs > 0 ? Math.round(daySpanMs / 86_400_000) : 0;

  return (
    <div className="space-y-6">
      {/* KPI hero */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCell
          label="TOTAL SPEND"
          value={<AnimatedUsd value={data.totalUsd} />}
          accent="emerald"
        />
        <KpiCell
          label="ACTIVE DAYS"
          value={<AnimatedNumber value={dayCount} />}
          sub={calendarSpanDays > 0 ? `of ${calendarSpanDays}` : undefined}
          accent="sky"
        />
        <KpiCell
          label="TOTAL TOKENS"
          value={
            <span className="tabular-nums">
              {formatAbbrevNumber(data.totalTokens, "round")}
            </span>
          }
          sub={`${formatAbbrevNumber(data.inputTokens, "round")} in / ${formatAbbrevNumber(data.outputTokens, "round")} out`}
          accent="violet"
        />
        <KpiCell
          label="CACHE READ"
          value={
            <span className="tabular-nums">
              {formatAbbrevNumber(data.cacheReadTokens, "round")}
            </span>
          }
          sub={`${formatAbbrevNumber(data.cacheWriteTokens, "round")} written`}
          accent="amber"
        />
      </div>

      {/* Daily bar chart */}
      <section>
        <SectionHeading label="DAILY BURN" meta={`${dayCount} days active`} />
        <DailyBarChart data={data.byDay} />
      </section>

      {/* Breakdowns */}
      <div className="grid gap-5 md:grid-cols-2">
        <section>
          <SectionHeading
            label="BY STAGE"
            meta={`${data.byStage.length} stages`}
          />
          <StageTable rows={data.byStage} total={data.totalUsd} />
        </section>
        <section>
          <SectionHeading
            label="BY MODEL"
            meta={`${data.byModel.length} models`}
          />
          <ModelTable rows={data.byModel} total={data.totalUsd} />
        </section>
      </div>

      {/* Footer date range */}
      {data.firstBucketMs !== null && data.lastBucketMs !== null && (
        <div className="flex items-center justify-between border-t border-emerald-400/15 pt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
          <span>
            FIRST · {new Date(data.firstBucketMs).toISOString().slice(0, 10)}
          </span>
          <span className="h-px w-8 bg-emerald-400/20" />
          <span>
            LAST · {new Date(data.lastBucketMs).toISOString().slice(0, 10)}
          </span>
        </div>
      )}
    </div>
  );
};

const KpiCell: FC<{
  label: string;
  value: React.ReactNode;
  sub?: string;
  accent: "emerald" | "sky" | "violet" | "amber";
}> = ({ label, value, sub, accent }) => {
  const colorMap = {
    amber: {
      border: "border-amber-500/40 dark:border-amber-400/30",
      text: "text-amber-700 dark:text-amber-300",
    },
    emerald: {
      border: "border-emerald-500/40 dark:border-emerald-400/30",
      text: "text-emerald-700 dark:text-emerald-300",
    },
    sky: {
      border: "border-sky-500/40 dark:border-sky-400/30",
      text: "text-sky-700 dark:text-sky-300",
    },
    violet: {
      border: "border-violet-500/40 dark:border-violet-400/30",
      text: "text-violet-700 dark:text-violet-300",
    },
  };
  const cls = colorMap[accent];
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`relative border ${cls.border} bg-white/60 p-3 dark:bg-slate-900/60`}
    >
      <CornerBracket position="tl" />
      <CornerBracket position="br" />
      <p className="font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-slate-600 dark:text-slate-400">
        {label}
      </p>
      <div
        className={`mt-1 font-mono text-2xl font-semibold tabular-nums ${cls.text}`}
      >
        {value}
      </div>
      {sub && (
        <p className="mt-0.5 font-mono text-[10px] tracking-tight text-slate-500">
          {sub}
        </p>
      )}
    </motion.div>
  );
};

const SectionHeading: FC<{ label: string; meta?: string }> = ({
  label,
  meta,
}) => (
  <div className="mb-2 flex items-baseline justify-between border-b border-emerald-500/25 pb-1.5 dark:border-emerald-400/10">
    <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700/90 dark:text-emerald-300/70">
      [ {label} ]
    </p>
    {meta && (
      <p className="font-mono text-[10px] tracking-tight text-slate-500">
        {meta}
      </p>
    )}
  </div>
);

const DailyBarChart: FC<{ data: DailyBucket[] }> = ({ data }) => {
  if (data.length === 0) {
    return (
      <p className="py-6 text-center font-mono text-[11px] text-slate-500">
        NO ACTIVITY YET
      </p>
    );
  }
  const max = Math.max(...data.map((d) => d.usd), 0.001);
  return (
    <div className="flex h-36 items-end gap-1">
      {data.map((d, i) => {
        const heightPct = Math.max(2, (d.usd / max) * 100);
        const dateLabel = new Date(d.dayStartMs).toISOString().slice(5, 10);
        return (
          <motion.div
            key={d.dayStartMs}
            className="group relative flex h-full flex-1 flex-col items-center justify-end"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: i * 0.02, duration: 0.2 }}
            title={`${dateLabel} · ${formatUsd(d.usd)}`}
          >
            <span className="pointer-events-none absolute -top-6 whitespace-nowrap font-mono text-[9px] tabular-nums text-emerald-700 opacity-0 transition-opacity group-hover:opacity-100 dark:text-emerald-300">
              {formatUsd(d.usd)}
            </span>
            <motion.div
              className="w-full bg-emerald-400/80 shadow-[0_0_12px_rgba(16,185,129,0.4)]"
              initial={{ height: "0%" }}
              animate={{ height: `${heightPct}%` }}
              transition={{
                damping: 16,
                delay: i * 0.02,
                stiffness: 120,
                type: "spring",
              }}
            />
            <span className="mt-1 font-mono text-[8px] tabular-nums text-slate-500 dark:text-slate-600">
              {dateLabel}
            </span>
          </motion.div>
        );
      })}
    </div>
  );
};

const StageTable: FC<{ rows: StageRow[]; total: number }> = ({
  rows,
  total,
}) => (
  <div className="border border-emerald-500/25 bg-slate-50/60 dark:border-emerald-400/10 dark:bg-slate-900/40">
    {rows.map((r) => {
      const pct = total > 0 ? (r.usd / total) * 100 : 0;
      const shortStage = r.stage.replace(/^arti\.chat\.v1\.?/, "") || "main";
      return (
        <div
          key={r.stage}
          className="relative flex items-center justify-between gap-3 border-b border-emerald-500/15 px-3 py-2 last:border-b-0 dark:border-emerald-400/5"
        >
          <motion.div
            className="absolute inset-y-0 left-0 bg-emerald-400/10"
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
          <div className="relative flex-1">
            <p className="font-mono text-[11px] uppercase tracking-widest text-slate-800 dark:text-slate-200">
              {shortStage}
            </p>
            <p className="mt-0.5 font-mono text-[9px] tracking-tight text-slate-500">
              {formatAbbrevNumber(r.tokens, "round")} tokens · {pct.toFixed(1)}%
            </p>
          </div>
          <p className="relative font-mono text-sm font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">
            {formatUsd(r.usd)}
          </p>
        </div>
      );
    })}
  </div>
);

const ModelTable: FC<{ rows: ModelRow[]; total: number }> = ({
  rows,
  total,
}) => (
  <div className="border border-emerald-500/25 bg-slate-50/60 dark:border-emerald-400/10 dark:bg-slate-900/40">
    {rows.map((r) => {
      const pct = total > 0 ? (r.usd / total) * 100 : 0;
      const shortModel = shortenModel(r.model);
      return (
        <div
          key={r.model}
          className="relative flex items-center justify-between gap-3 border-b border-emerald-500/15 px-3 py-2 last:border-b-0 dark:border-emerald-400/5"
        >
          <motion.div
            className="absolute inset-y-0 left-0 bg-violet-400/10"
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
          <div className="relative min-w-0 flex-1">
            <p className="truncate font-mono text-[11px] uppercase tracking-widest text-slate-800 dark:text-slate-200">
              {shortModel}
            </p>
            <p className="mt-0.5 font-mono text-[9px] tracking-tight text-slate-500">
              {formatAbbrevNumber(r.cacheReadTokens, "round")} cache read ·{" "}
              {formatAbbrevNumber(r.cacheWriteTokens, "round")} written
            </p>
          </div>
          <p className="relative font-mono text-sm font-semibold tabular-nums text-violet-700 dark:text-violet-300">
            {formatUsd(r.usd)}
          </p>
        </div>
      );
    })}
  </div>
);

function shortenModel(model: string): string {
  if (model.includes("claude-haiku")) {
    return "haiku";
  }
  if (model.includes("claude-sonnet")) {
    return "sonnet";
  }
  if (model.includes("claude-opus")) {
    return "opus";
  }
  const arnMatch = /inference-profile\/([a-z0-9]+)/i.exec(model);
  if (arnMatch) {
    return `bedrock:${arnMatch[1].slice(0, 8)}`;
  }
  return model.length > 32 ? `…${model.slice(-29)}` : model;
}

const AnimatedUsd: FC<{ value: number }> = ({ value }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const spring = useSpring(0, { damping: 24, mass: 0.8, stiffness: 120 });
  useEffect(() => {
    spring.set(value);
  }, [spring, value]);
  useEffect(() => {
    const unsub = spring.on("change", (latest) => {
      if (ref.current) {
        ref.current.textContent = formatUsd(latest);
      }
    });
    return () => unsub();
  }, [spring]);
  return <span ref={ref}>{formatUsd(0)}</span>;
};

const AnimatedNumber: FC<{ value: number }> = ({ value }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const spring = useSpring(0, { damping: 24, mass: 0.8, stiffness: 160 });
  useEffect(() => {
    spring.set(value);
  }, [spring, value]);
  useEffect(() => {
    const unsub = spring.on("change", (latest) => {
      if (ref.current) {
        ref.current.textContent = String(Math.round(latest));
      }
    });
    return () => unsub();
  }, [spring]);
  return <span ref={ref}>0</span>;
};
